import type { ListAudioUrlParams } from '@listening/api-contract';
import {
  readyAudioStatuses,
  readyAudioStatusSet,
} from '@listening/api-contract/audio-statuses';
import { AsyncResult, Atom } from 'effect/reactivity';
import { Duration, Effect, Option, Schema } from 'effect';
import {
  type LibrarySortMode,
  librarySortParams,
} from '../stores/library-sort-store';
import { pollEvery, ssrApiInfiniteQuery } from './api-runtime';
import { type AudioItem, AudioItems } from './audio-items';
import {
  type AudioListField,
  audioKeys,
  audioRowAtom,
  pendingAudioIdsAtom,
  syncedAudioRowAtom,
  writeAudios,
} from './audio-store';
import { isActiveProcessingStatus } from './processing-queue-polling';
import type { ReactivityKey } from './reactivity-keys';

export { readyAudioStatusSet } from '@listening/api-contract/audio-statuses';

// The fixed, named set of audio lists. Each is a server-ordered window whose
// rows live in the audio store (`audio-store.ts`), so an edit shows in every
// list at once. A list refetches when a change touches a field its filter or
// order reads; until then it hides rows that no longer match it, and shows
// rows with a write in flight that now do at its top.

type ListAudioParams = typeof ListAudioUrlParams.Type;

const listStaleTime = Duration.hours(24);

// Background poll so the always-mounted surfaces (library, processing queue,
// continue listening) stay live without a manual refresh: a conversion
// finishes server-side, or another client uploads. Polling pauses while the
// app is in the background.
//
// COST: refetching a paged list re-fetches every loaded page, so a
// deep-scrolled library re-fetches every loaded page each interval. The proper
// fix is delta-based polling (a cheap "did anything change?" check that only
// invalidates on a hit) — tracked in
// docs/projects/backlog/backend/list-and-get-audio-endpoints-v3.md (#8).
const listPollIntervalMs = 20_000;
// While the live-updates stream is open, server events drive refetches and the
// poll only covers events that never arrive.
const liveUpdatesFallbackPollIntervalMs = 5 * 60_000;
const activeProcessingPollIntervalMs = 5_000;
let usesExternalAudioListPolling = false;

/** Mobile polls the lists itself, on Flutter's foreground cadence ramp. */
export function setExternalAudioListPollingEnabled(enabled: boolean) {
  usesExternalAudioListPolling = enabled;
}

/** Whether a live-updates stream that every writer publishes to is open.
 * Lists poll at the fallback interval while it is. */
export const liveUpdatesConnectedAtom = Atom.make(false).pipe(Atom.keepAlive);

const listPollInterval = (read: <B>(atom: Atom.Atom<B>) => B) =>
  read(liveUpdatesConnectedAtom)
    ? liveUpdatesFallbackPollIntervalMs
    : listPollIntervalMs;

const pageSize = 50;

const AudioListVersions = Schema.Struct({
  ttsFinalizedVersion: Schema.Finite,
  voiceDataVersion: Schema.Finite,
});

export type AudioListVersions = typeof AudioListVersions.Type;

/** One page of a list: its audios' ids, whose rows live in the store. */
const AudioIdPage = Schema.Struct({
  ids: Schema.Array(Schema.String),
  nextCursor: Schema.UndefinedOr(Schema.String),
  versions: Schema.UndefinedOr(AudioListVersions),
});

type AudioIdPage = typeof AudioIdPage.Type;

const AudioListCursor = Schema.Struct({
  cursor: Schema.optional(Schema.String),
});

export type AudioList = {
  readonly items: ReadonlyArray<AudioItem>;
  readonly hasNextPage: boolean;
  readonly loadingMore: boolean;
  /** The TTS and voice versions the first page reported. */
  readonly versions: AudioListVersions | undefined;
};

type AudioListSpec = {
  readonly params: ListAudioParams;
  readonly limit?: number;
  /** The fields this list's filter and order read. */
  readonly fields: ReadonlyArray<AudioListField>;
  readonly matches: (audio: AudioItem) => boolean;
  readonly staleTime?: Duration.Input;
  readonly reactivityKeys?: ReadonlyArray<ReactivityKey>;
};

const fetchAudioIdPage = (params: ListAudioParams) =>
  AudioItems.listPageEffect(params).pipe(
    Effect.tap((page) => writeAudios(page.items)),
    Effect.map((page): AudioIdPage => ({
      ids: page.items.map((audio) => audio.id),
      nextCursor: page.nextCursor,
      versions:
        page.ttsFinalizedVersion === undefined ||
        page.voiceDataVersion === undefined
          ? undefined
          : {
              ttsFinalizedVersion: page.ttsFinalizedVersion,
              voiceDataVersion: page.voiceDataVersion,
            },
    })),
  );

/** A list of audios, and the source a route loader starts for it. */
const audioList = (spec: AudioListSpec) => {
  const params = { ...spec.params, limit: spec.limit ?? pageSize };
  const { list: pages, source } = ssrApiInfiniteQuery({
    initialParam: {},
    fetchPage: ({ cursor }: typeof AudioListCursor.Type) =>
      fetchAudioIdPage({
        ...params,
        ...(cursor === undefined ? {} : { cursor }),
      }),
    key: `audio-list:${JSON.stringify(params)}`,
    page: AudioIdPage,
    param: AudioListCursor,
    nextParam: (page) =>
      page.nextCursor === undefined ? undefined : { cursor: page.nextCursor },
    staleTime: spec.staleTime ?? listStaleTime,
    reactivityKeys: spec.reactivityKeys ?? [
      audioKeys.lists,
      audioKeys.removed,
      ...spec.fields.map(audioKeys.field),
    ],
  });
  const list = Atom.writable(
    (get) =>
      AsyncResult.map(get(pages), (value): AudioList => {
        const ids = value.pages.flatMap((page) => page.ids);
        const listed = new Set(ids);
        const arriving = [...get(pendingAudioIdsAtom)].filter((id) => {
          if (listed.has(id)) return false;
          const synced = get(syncedAudioRowAtom(id));
          return synced !== undefined && !spec.matches(synced);
        });
        return {
          items: [...arriving, ...ids]
            .flatMap((id) => get(audioRowAtom(id)) ?? [])
            .filter(spec.matches),
          hasNextPage: value.hasNextPage,
          loadingMore: value.loadingMore,
          versions: value.pages[0]?.versions,
        };
      }),
    (ctx) => ctx.set(pages, undefined),
    (refresh) => refresh(pages),
  );
  return { list, source };
};

const isListed = (audio: AudioItem) => audio.archived_at_ms === null;
const isReady = (audio: AudioItem) =>
  isListed(audio) && readyAudioStatusSet.has(audio.conversion_status);

const sortField = (mode: LibrarySortMode): AudioListField =>
  librarySortParams(mode).sort_by === 'title'
    ? 'title_sort_key'
    : 'created_at_ms';

const pollUnlessExternal = pollEvery<AudioList>((_list, _failed, read) =>
  usesExternalAudioListPolling ? false : listPollInterval(read),
);

const libraryList = Atom.family((sortMode: LibrarySortMode) => {
  const { list, source } = audioList({
    params: { ...librarySortParams(sortMode), status: readyAudioStatuses },
    fields: ['conversion_status', 'archived_at_ms', sortField(sortMode)],
    matches: isReady,
  });
  return { list: list.pipe(pollUnlessExternal), source };
});

/** Every ready audio, in the user's library order. */
export const libraryListAtom = (sortMode: LibrarySortMode) =>
  libraryList(sortMode).list;

/** The first `limit` ready audios, for surfaces that never page. */
export const libraryHeadAtom = Atom.family(
  ({ limit, sortMode }: { limit: number; sortMode: LibrarySortMode }) =>
    audioList({
      params: { ...librarySortParams(sortMode), status: readyAudioStatuses },
      limit,
      fields: ['conversion_status', 'archived_at_ms', sortField(sortMode)],
      matches: isReady,
    }).list.pipe(pollUnlessExternal),
);

const processingQueue = audioList({
  params: { sort_by: 'created_at', except_status: readyAudioStatuses },
  fields: ['conversion_status', 'archived_at_ms', 'created_at_ms'],
  matches: (audio) =>
    isListed(audio) && !readyAudioStatusSet.has(audio.conversion_status),
});

/** What a route loader starts for `processingQueueAtom`. */
export const processingQueueSource = processingQueue.source;

/** Audios still converting, or failed, newest first. Polls every 5 seconds
 * while one is actively converting. */
export const processingQueueAtom = processingQueue.list.pipe(
  pollEvery<AudioList>((list, _failed, read) =>
    usesExternalAudioListPolling
      ? Option.exists(list, ({ items }) =>
          items.some((audio) =>
            isActiveProcessingStatus(audio.conversion_status),
          ),
        )
        ? activeProcessingPollIntervalMs
        : false
      : listPollInterval(read),
  ),
);

type FolderAudios = {
  readonly folderId: string;
  readonly sortMode: LibrarySortMode;
};

const folderAudios = Atom.family(({ folderId, sortMode }: FolderAudios) => {
  const { list, source } = audioList({
    params: {
      ...librarySortParams(sortMode),
      status: readyAudioStatuses,
      folder_id: folderId,
    },
    fields: [
      'conversion_status',
      'archived_at_ms',
      'folder_id',
      sortField(sortMode),
    ],
    matches: (audio) =>
      isReady(audio) &&
      audio.folder_id === (folderId === 'root' ? null : folderId),
  });
  return {
    list: folderId === 'root' ? list.pipe(pollUnlessExternal) : list,
    source,
  };
});

/** The ready audios in one folder; `'root'` is the unfiled ones. */
export const folderAudiosAtom = (folder: FolderAudios) =>
  folderAudios(folder).list;

/** What a route loader starts for `folderAudiosAtom`. */
export const folderAudiosSource = (folder: FolderAudios) =>
  folderAudios(folder).source;

type LibraryView = {
  readonly sortMode: LibrarySortMode;
  readonly unfiledOnly: boolean;
};

const libraryView = ({ sortMode, unfiledOnly }: LibraryView) =>
  unfiledOnly
    ? folderAudios({ folderId: 'root', sortMode })
    : libraryList(sortMode);

/** The library as home shows it: every ready audio, or only the unfiled ones. */
export const libraryViewAtom = (view: LibraryView) => libraryView(view).list;

/** What a route loader starts for `libraryViewAtom`. */
export const libraryViewSource = (view: LibraryView) =>
  libraryView(view).source;

/**
 * Ready audios whose title matches `titleQuery`, A to Z. The server matches
 * the needle, so a needle nobody matches costs one request instead of walking
 * the whole library. It refetches only on a delete: the rows a picker shows
 * stay put while the user files them.
 */
export const folderPickerAtom = Atom.family(
  (titleQuery: string) =>
    audioList({
      params: {
        sort_by: 'title',
        status: readyAudioStatuses,
        title_query: titleQuery,
      },
      fields: [],
      matches: isReady,
      staleTime: Duration.zero,
      reactivityKeys: [audioKeys.removed],
    }).list,
);

const continueListening = audioList({
  params: { sort_by: 'played_at', status: readyAudioStatuses },
  limit: 10,
  fields: ['conversion_status', 'archived_at_ms', 'played_at_ms'],
  matches: isReady,
});

/** What a route loader starts for `continueListeningAtom`. */
export const continueListeningSource = continueListening.source;

/**
 * The most recently played ready audios that the user has started, most
 * recent first. The server returns the 10 most recently played;
 * `selectContinueListening` drops never-started padding.
 */
export const continueListeningAtom = continueListening.list.pipe(
  pollUnlessExternal,
  Atom.map((result) =>
    AsyncResult.map(result, (list) =>
      AudioItems.selectContinueListening(list.items),
    ),
  ),
);

const recentUploads = audioList({
  params: { sort_by: 'created_at', except_status: 'Error' },
  limit: 50,
  fields: ['conversion_status', 'archived_at_ms', 'created_at_ms'],
  matches: (audio) => isListed(audio) && audio.conversion_status !== 'Error',
});

/** What a route loader starts for `recentUploadsAtom`. */
export const recentUploadsSource = recentUploads.source;

/** One bounded page of non-errored audios, newest first, for the free-plan
 * upload counter; the caller filters by week start. */
export const recentUploadsAtom = recentUploads.list.pipe(
  Atom.map((result) => AsyncResult.map(result, (list) => list.items)),
);
